import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';
import config from 'config/index';

import { EUserType } from 'types/accessControll.types';
import type { TOutreachCandidate } from 'types/outreachCandidates.types';

const reducerPath = 'outreach' as const;
export const path = 'v1/outreach' as const;

export type InsertOutreachCandidatePayload = Omit<
  TOutreachCandidate,
  'id' | 'createdAt' | 'qualifiedAt' | 'campaignId'
>;

export const outreachCandidatesApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  tagTypes: ['OutReachCandidate'],
  endpoints: (builder) => ({
    findOutreachCandidates: builder.query<
      TOutreachCandidate[],
      { clientId?: string; campaignId?: string; qualificationStatus?: string; callStatus?: string }
    >({
      query: (payload) => ({
        url: `/candidates/find`,
        method: 'POST',
        data: payload,
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
      providesTags: ['OutReachCandidate'],
      transformResponse: (resp) => resp.data,
    }),
    getOutreachCandidatesById: builder.query<TOutreachCandidate, string>({
      query: (outreachCandidateId) => ({
        url: `/candidates/${outreachCandidateId}`,
        method: 'GET',
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
      providesTags: (_result, _error, id) => [{ type: 'OutReachCandidate', id }],
      transformResponse: (resp) => resp.data,
    }),

    // Mutations
    // Insert outreach candidate
    insertOutreachCandidate: builder.mutation<
      TOutreachCandidate[],
      { campaignId: string; candidates: InsertOutreachCandidatePayload[] }
    >({
      query: ({ campaignId, candidates }) => ({
        url: `/campaigns/${campaignId}/candidates`,
        method: 'POST',
        data: candidates,
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
      invalidatesTags: ['OutReachCandidate'],
      transformResponse: (resp) => resp.data,
    }),

    resetOutreachCandidateStatus: builder.mutation<TOutreachCandidate, string>({
      query: (outreachCandidateId) => ({
        url: `/candidates/${outreachCandidateId}/reset-status`,
        method: 'POST',
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'OutReachCandidate', id }],
      transformResponse: (resp) => resp.data,
    }),
  }),
});

export const {
  // Queries
  useFindOutreachCandidatesQuery,
  useGetOutreachCandidatesByIdQuery,

  // Mutations
  useInsertOutreachCandidateMutation,
  useResetOutreachCandidateStatusMutation,
} = outreachCandidatesApi;
